<template>
  <div class="search-container" v-click-outside="closeSearchResults">
    <div class="search-bar">
      <el-select
        v-model="selectedCategory"
        class="search-category"
        placeholder="选择搜索类别"
        @change="handleCategoryChange"
      >
        <el-option
          v-for="item in searchCategories"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
      
      <el-input
        v-model="searchQuery"
        class="search-input"
        placeholder="请输入搜索内容"
        :suffix-icon="Search"
        @keyup.enter="handleSearch"
      >
        <template #append>
          <el-button :icon="Search" @click="handleSearch">搜索</el-button>
        </template>
      </el-input>
    </div>

    <!-- 搜索结果浮窗 -->
    <Transition name="fade">
      <div v-if="hasSearched" class="search-results-popup" v-loading="loading">
        <div class="search-results-header">
          <span class="search-results-title">搜索结果</span>
          <el-button type="text" @click="closeSearchResults">
            <el-icon><Close /></el-icon>
          </el-button>
        </div>
        <SearchBySearch
          :category="selectedCategory"
          :query="searchQuery"
          :results="searchResults"
          :loading="loading"
          @close="closeSearchResults"
        />
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { Search, Close } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { searchOdoo } from '@/utils/odooApi'
import SearchBySearch from './SearchBySearch.vue'

// 点击外部指令
const vClickOutside = {
  mounted(el, binding) {
    el._clickOutside = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event)
      }
    }
    document.addEventListener('click', el._clickOutside)
  },
  unmounted(el) {
    document.removeEventListener('click', el._clickOutside)
  }
}

const searchCategories = [
  { label: '订单', value: 'tmys.order' },
  { label: '服装产品', value: 'tmys.product.cloth' },
  { label: '面料产品', value: 'tmys.product.material' },
  { label: '辅料产品', value: 'tmys.product.auxiliary' },
  { label: '设备产品', value: 'tmys.product.equip' }
]

const selectedCategory = ref('')
const searchQuery = ref('')
const loading = ref(false)
const hasSearched = ref(false)
const searchResults = ref([])

// 关闭搜索结果
const closeSearchResults = () => {
  hasSearched.value = false
  searchResults.value = []
}

// 处理类别变化
const handleCategoryChange = (value) => {
  searchQuery.value = ''
  // 根据不同类别设置不同的搜索提示
  const placeholderMap = {
    'tmys.order': '请输入产品',
    'tmys.product.cloth': '请输入服装名称',
    'tmys.product.fabric': '请输入面料名称',
    'tmys.product.accessory': '请输入辅料名称',
    'tmys.product.equipment': '请输入设备名称'
  }
  const input = document.querySelector('.search-input input')
  if (input) {
    input.placeholder = placeholderMap[value] || '请输入搜索内容'
  }
}

// 处理搜索
const handleSearch = async () => {
  if (!selectedCategory.value) {
    ElMessage.warning('请选择搜索类别')
    return
  }
  
  if (!searchQuery.value.trim()) {
    ElMessage.warning('请输入搜索内容')
    return
  }

  loading.value = true
  try {
    // 调用Odoo搜索API
    const results = await searchOdoo(
      selectedCategory.value,
      searchQuery.value.trim()
    )
    
    // 更新搜索结果
    searchResults.value = results
    hasSearched.value = true
  } catch (error) {
    ElMessage.error(error.message || '搜索失败')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.search-container {
  position: relative;
  width: 92%;
}

.search-bar {
  display: flex;
  gap: 8px;
  width: 100%;
  background: #fff;
  padding: 4px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.search-category {
  width: 120px;
}

.search-input {
  flex: 1;
}

/* 搜索结果浮窗样式 */
.search-results-popup {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  max-height: 80vh;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
}

.search-results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
}

.search-results-title {
  font-size: 16px;
  font-weight: 500;
  color: #2c3e50;
}

/* 动画效果 */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

:deep(.el-select .el-input__wrapper) {
  background-color: #f5f7fa;
}

:deep(.el-input__wrapper) {
  background-color: #f5f7fa;
}

:deep(.el-input-group__append) {
  background-color: #409EFF;
  border: none;
  padding: 0 16px;
}

:deep(.el-input-group__append .el-button) {
  color: #fff;
  border: none;
  font-weight: 500;
}

@media screen and (max-width: 768px) {
  .search-bar {
    flex-direction: row;
    gap: 8px;
  }

  .search-category {
    width: 100px;
  }

  .search-results-popup {
    position: fixed;
    top: 120px;
    left: 10px;
    right: 10px;
    max-height: calc(100vh - 140px);
  }
}
</style>